import * as R from "ramda";

import {
  deepFindPathByUID,
  generateNode,
  newSetNodeKVPairAction,
  deepFindAllNodes,
} from "./doc_helpers";

import {
  now,
  stringToDate,
} from "./effective_dates";

export function addActivitiesToDocument(json: SectionNode, currentActivities: Array<CombineActivitiesReturnType>, activityUserStatesByActivityId: Record<number, CurrentActivityUserState>) {
  const groupedActivities = R.groupBy((activity: CombineActivitiesReturnType) => {
    return [
      activity.placement_uid,
      activity.placement_before,
      activity.placement_inside,
    ].toString();
  }, currentActivities);

  Object.values(groupedActivities).forEach((groupOfActivities) => {
    if (!groupOfActivities) { return; }

    const sortedGroupOfActivities = R.sortBy((activity) => activity.order, groupOfActivities);
    const activitiesToInsert: Array<ActivityNode> = sortedGroupOfActivities.map((activity) => (
      {
        ...activity,
        type: "activity",
        uid: activity.id.toString(), // TODO: FIGURE OUT UID TRANSITION
        activityUserState: activityUserStatesByActivityId[activity.id],
        content: activityContentToFlatBlockContent(activity, activityUserStatesByActivityId[activity.id]),
      }
    ));

    const firstActivity = groupOfActivities[0];
    const placementUid = firstActivity.placement_uid;
    const placementBefore = firstActivity.placement_before;
    const placementInside = firstActivity.placement_inside;

    let activitiesPath: Array<string | number>;
    if (placementUid === "video") {
      const firstLevelContent = json.content;
      const videoIndex = R.findIndex((node) => "video" === node.type, firstLevelContent);

      activitiesPath = ["content", placementBefore ? videoIndex : videoIndex + 2]; // ADD 2 TO BE AFTER BUTTON
    } else if(!placementUid) {
      // FIND LAST SECTION
      const firstLevelContent = json.content;
      const lastSectionIndex = R.findLastIndex((n) => ["section", "activity"].includes(n.type), firstLevelContent);
      activitiesPath = ["content", lastSectionIndex + 1];
    } else {
      const placementNodePath = deepFindPathByUID(json, placementUid);

      if (!placementNodePath) {
        // TODO: ADD ERROR
        return;
      }

      activitiesPath = placementNodePath;
      if (placementInside) {
        activitiesPath = [...activitiesPath, "content"];

        if (placementBefore) {
          activitiesPath = [...activitiesPath, 0];
        } else {
          const lengthOfContent = (R.path(activitiesPath, json) as Array<unknown>).length;
          activitiesPath = [...activitiesPath, lengthOfContent];
        }
      } else {
        activitiesPath = placementBefore ? placementNodePath : R.adjust(-1, R.inc, placementNodePath as Array<number>);
      }
    }

    json = R.over(R.lensPath(R.init(activitiesPath)), R.insertAll(R.last(activitiesPath) as number, activitiesToInsert), json);
  });

  return json;
}

export function combineActivities(
    currentDocumentSetDocumentActivities: Array<ItemAndActionsFromStore<DocumentSetDocumentActivitySnakeModel, {}, {}>>,
    currentDocumentActivities: Array<ItemAndActionsFromStore<DocumentActivitySnakeModel, {}, {}>>,
    currentDocumentActivityAmendments: Array<ItemAndActionsFromStore<DocumentActivityAmendmentSnakeModel, {}, {}>>,
    addableActivities: Array<ItemAndActionsFromStore<ActivitySnakeModel, {}, {}>>,
  ) {
  let cars: Array<CombineActivitiesReturnType> = currentDocumentSetDocumentActivities.filter((dsds) => "lastSyncedAt" in dsds).map((dsda) => {
    return {
      ...dsda.activity,
      document_set_document_activity: dsda,
      show_after: dsda.show_after,
      show_until: dsda.show_until,
      attempt_index: dsda.attempt_index,
      custom_entries: dsda.custom_entries,
    };
  });

  cars = cars.concat(currentDocumentActivities.filter((da) => "lastSyncedAt" in da).map((da) => {
    const daa = currentDocumentActivityAmendments.filter((daa) => "lastSyncedAt" in daa).find((daa) => daa.document_activity_id == da.id);

    return {
      ...da.activity,
      document_activity: da,
      document_activity_amendment: daa,
      show_after: daa ? daa.show_after : da.show_after,
      show_until: daa ? daa.show_until : da.show_until,
      attempt_index: da.attempt_index,
      custom_entries: daa ? {...da.custom_entries, ...daa.custom_entries} : da.custom_entries,
    };
  }));

  const existingActivityIds = cars.map((activity) => activity.id);
  cars = cars.concat((addableActivities.filter((da) => "lastSyncedAt" in da) as Array<ActivitySnakeModel>).filter((activity) => !existingActivityIds.includes(activity.id)).map((activity) => {
    return {
      ...activity,
      show_after: undefined,
      show_until: "1970-01-01T00:00:00.000Z",
      attempt_index: 0,
      custom_entries: {},
    };
  }));

  return cars;
}

export function filterActivitiesBySchedule(activities: Array<CombineActivitiesReturnType>) {
  return activities.filter((activity) => {
    const {
      show_after,
      show_until,
    } = activity;

    const showAfter = stringToDate(show_after);
    const showUntil = stringToDate(show_until);

    return (!showAfter || showAfter < now()) && (!showUntil || showUntil > now()) && ("document_set_document_activity" in activity || "document_activity" in activity);
  });
}

export function hasCustomFields(activityContent: ActivityContent) {
  return !R.isEmpty(findCustomFields(activityContent));
}

const AI_CHAT_CUSTOMIZABLE_ATTRIBUTES = [
  "aiInstructions",
  "assistantDisplayImage",
  "assistantDisplayName",
  "userDisplayImage",
  "userDisplayName",
] as const;
export function findCustomFields(activityContent: ActivityContent) {
  const returnFieldMap: Record<string, string> = {};

  const namedRegex = customFieldsRegex();

  const openAiChats: Array<OpenAiChatNode> = deepFindAllNodes(activityContent, (node) => node.type === "openAiChat") as any;

  openAiChats.forEach((openAiChat) => {
    AI_CHAT_CUSTOMIZABLE_ATTRIBUTES.map((attribute) => attribute);

    AI_CHAT_CUSTOMIZABLE_ATTRIBUTES.forEach((attribute) => {
      const matches = openAiChat[attribute]?.matchAll(namedRegex) || [];
      for (const match of matches) {
        if (!returnFieldMap[match[1].trim()]) {
          returnFieldMap[match[1].trim()] = (match[3] || "").trim();
        }
      }

    });
  });

  return returnFieldMap;
}

export function customFieldsRegex(key: string = "([^(\\|)(\\]\\]\\])]+)") {
  return RegExp(`\\[\\[\\[${key}\\s*(\\|\\s*([^(\\]\\]\\])]*))?\\]\\]\\]`, "g");
}

export function replaceCustomFieldWithCustomEntries(input: string, customEntries: Record<string, string>) {
  let output = input;

  Object.entries(customEntries).forEach(([key, value]) => {
    const regex = customFieldsRegex(key);
    output = output.replace(regex, value);
  });

  const replaceRestRegex = customFieldsRegex("[^(\\|)((\\]\\]\\])]+");
  output = output.replace(replaceRestRegex, "$2");

  return output;
}

function isArrayArray(arr: ActivityContent): arr is BlockContent[][] {
  return arr[0] && Array.isArray(arr[0])
}

function activityContentToFlatBlockContent(activity: CombineActivitiesReturnType, activityUserState: CurrentActivityUserState): Array<SectionNode> {
  const {
    content,
    placement_inside,
    is_graded,
    name,
  } = activity;

  const activitySections: Array<SpecialSectionNode> = isArrayArray(content) ? (
    [{
      type: "section" as const,
      uid: "root",
      title: name,
      content: content,
      classes: [],
      level: placement_inside ? 2 : 1,
    }]
  ) : content.map((section) => ({...section, content: [section.content]}));

  return activitySections.map((activitySection, activitySectionIndex) => {
    const isFirstSection = activitySectionIndex === 0;
    const isLastSection = activitySectionIndex === activitySections.length - 1;

    const activityBlocks = activitySection.content;
    const activityBlocksReturn = activityBlocks.map((activityBlock, activityBlockIndex) => {
      const isFirstBlock = activityBlockIndex === 0;
      const isLastBlock = activityBlockIndex === activityBlocks.length - 1;

      const activityBlockInputNodes = activityBlock.filter((node) => ["textInput", "multipleChoice", "openAiChat"].includes(node.type));

      const buttonUid = isLastBlock && isLastSection ? "submit_button" : (isFirstSection ? `submit_button_${activityBlockIndex}` : `submit_button_${activitySectionIndex}_${activityBlockIndex}`);
      let buttonActions = activityBlockInputNodes.map((inputNode) => newSetNodeKVPairAction(inputNode.uid, "inactive", 2));

      if (is_graded) {
        buttonActions = buttonActions.concat(activityBlockInputNodes.map((inputNode) => newSetNodeKVPairAction(inputNode.uid, "graded", true)));
      }

      if (isLastBlock) {
        if (is_graded) {
          const scoreableNodes = activityBlocks.flatMap((ab) => ab.filter((node) => node.type === "multipleChoice"));
          const nodeScores = scoreableNodes.map((scorableNode) => {
            const isCorrect = "lastSyncedAt" in activityUserState && scorableNode.content.every((answerOption) => !!answerOption.answer === !!activityUserState.state?.[answerOption.uid]?.value?.value);

            return isCorrect ? 1.0 : 0.0;
          });

          if (nodeScores.length > 0) {
            const grade_percentage = R.sum(nodeScores) / nodeScores.length;
            buttonActions = buttonActions.concat(newSetNodeKVPairAction("root", "grade_percentage", grade_percentage));
          }
        }

        const otherActivityBlocks = R.init(activityBlocks);
        const otherActivityNodes = otherActivityBlocks.flat().filter((node) => node.type !== "button");
        const showOtherActivityNodesActions = otherActivityNodes.map((node) => newSetNodeKVPairAction(node.uid, "invisible", false));

        buttonActions = buttonActions.concat(
          newSetNodeKVPairAction(buttonUid, "inactive", 2),
          ...showOtherActivityNodesActions,
        );

        if (!isLastSection) {
          const nextActivitySection = activitySections[activitySectionIndex + 1];
          const showNextSection = [
            newSetNodeKVPairAction(nextActivitySection.uid, "invisible", false),
            newSetNodeKVPairAction(nextActivitySection.uid, "collapsed", false),
          ];

          buttonActions = buttonActions.concat(showNextSection);
        }
      } else {
        const hideCurrentNodes = activityBlock.map((node) => newSetNodeKVPairAction(node.uid, "invisible", true));

        const nextActivityBlock = activityBlocks[activityBlockIndex + 1];
        const showNextNodes = nextActivityBlock.map((node) => newSetNodeKVPairAction(node.uid, "invisible", false));

        const isNextActivityBlockLast = activityBlockIndex + 1 === activityBlocks.length - 1;
        const nextNextActivityButtonUid = isNextActivityBlockLast && isLastSection ? "submit_button" : (isFirstSection ? `submit_button_${activityBlockIndex + 1}` : `submit_button_${activitySectionIndex}_${activityBlockIndex + 1}`);

        buttonActions = buttonActions.concat(
          ...hideCurrentNodes,
          newSetNodeKVPairAction(buttonUid, "invisible", true),
          ...showNextNodes,
          newSetNodeKVPairAction(nextNextActivityButtonUid, "invisible", false),
        );
      }

      const submitButtonTitle = activitySection.submitButtonTitle;

      activityBlock = activityBlock.concat([
        generateNode("button", {
          uid: buttonUid,
          title: isLastBlock && submitButtonTitle ? submitButtonTitle : "Submit",
          invisible: !isFirstBlock,
          onClick: buttonActions,
        }),
      ]);

      return activityBlock;
    });

    return {
      ...activitySection,
      content: activityBlocksReturn.flat(),
      nested: true,
      collapsible: !placement_inside,
      collapsed: !placement_inside,
      // inactive: inactive, \\ TODO: FIX
      // inactiveBoolean: inactiveBoolean, \\ TODO: FIX
      invisible: !isFirstSection,
    };
  });
}

export function activityContentToSection(content: ActivityContent, sectionTitle: string) {
  const flattendContent = isArrayArray(content) ? content.flat() : content;

  return {
    type: "section" as const,
    uid: "root",
    title: sectionTitle,
    content: flattendContent.map((node) => ({...node, collapsible: true, nested: true, collapsed: false})), // TODO: Check if ok to use
    classes: [],
  };
}
